import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Services.css";
import SidePanel from "./SidePanel";
import { apiFetch } from "../utils/api";
import { canDo } from "../utils/permissions";
import Paginator from "./Paginator";

const PRICE_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed" },
  { value: "from", label: "From" },
  { value: "free", label: "Free" }
];

const Services = ({ currentUser }) => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    price: "",
    priceType: "fixed",
    durationMinutes: "30",
    category: "",
    treatmentType: "",
    note: ""
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [recordAudit, setRecordAudit] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priceTypeFilter, setPriceTypeFilter] = useState("");
  const [servicesPage, setServicesPage] = useState(1);
  const [servicesPageSize, setServicesPageSize] = useState(25);
  const submitInFlightRef = useRef(false);

  const openPanel = () => setPanelOpen(true);
  const closePanel = () => {
    setPanelOpen(false);
    setEditingId(null);
    setRecordAudit(null);
    setForm({
      name: "",
      price: "",
      priceType: "fixed",
      durationMinutes: "30",
      category: "",
      treatmentType: "",
      note: ""
    });
  };

  const loadServices = () => {
    apiFetch("/api/services")
      .then(res => res.json())
      .then(data => setServices(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load services", err));
  };

  const loadCategories = () => {
    apiFetch("/api/service-categories")
      .then(res => res.json())
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load service categories", err));
  };

  const loadTreatmentTypes = () => {
    apiFetch("/api/treatment-types")
      .then(res => res.json())
      .then(data => setTreatmentTypes(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load treatment types", err));
  };

  useEffect(() => {
    loadServices();
    loadCategories();
    loadTreatmentTypes();
  }, []);

  useEffect(() => {
    if (form.priceType === "free" && form.price !== "0") {
      setForm(prev => ({ ...prev, price: "0" }));
    }
  }, [form.priceType, form.price]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) return;
    
    submitInFlightRef.current = true;
    setIsSaving(true);

    const releaseSubmitLock = () => {
      submitInFlightRef.current = false;
      setIsSaving(false);
    };

    try {
      let saved;
      if (editingId) {
        // update existing
        const res = await apiFetch(`/api/services/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(form)
        });
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        saved = await res.json();
        setServices(services.map(s => ((s.publicId || s.id) === editingId ? saved : s)));
      } else {
        const res = await apiFetch("/api/services", {
          method: "POST",
          body: JSON.stringify(form)
        });
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        saved = await res.json();
        setServices([saved, ...services]);
      }
      closePanel();
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to save service", err);
      alert("Could not save service");
      releaseSubmitLock();
    }
  };

  const renderPriceLabel = service => {
    const value = Number(service.price || 0);
    const label = String(service.priceType || "fixed").toLowerCase();
    if (label === "free") return "Free";
    if (label === "from") return `From ${value}`;
    return `${value}`;
  };

  const filteredServices = useMemo(() => {
    let result = services;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.category || "").toLowerCase().includes(q) ||
        (s.treatmentType || "").toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      result = result.filter(s => (s.category || "") === categoryFilter);
    }
    if (priceTypeFilter) {
      result = result.filter(s => String(s.priceType || "fixed").toLowerCase() === priceTypeFilter);
    }
    return result;
  }, [services, search, categoryFilter, priceTypeFilter]);

  useEffect(() => { setServicesPage(1); }, [filteredServices]);

  const pagedServices = useMemo(
    () => filteredServices.slice((servicesPage - 1) * servicesPageSize, servicesPage * servicesPageSize),
    [filteredServices, servicesPage, servicesPageSize]
  );

  return (
    <div className="services-page page main-content">
      <div className="services-header">
        <div>
          <h1>Services</h1>
          <p>Define treatments, pricing models, durations, and category-wise service catalog.</p>
        </div>
        <div className="services-actions">
          <input
            className="services-search-input"
            type="text"
            placeholder="Search by name, category or treatment"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="services-filter-select"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
            ))}
          </select>
          <select
            className="services-filter-select"
            value={priceTypeFilter}
            onChange={e => setPriceTypeFilter(e.target.value)}
          >
            <option value="">All Price Types</option>
            <option value="fixed">Fixed</option>
            <option value="from">From</option>
            <option value="free">Free</option>
          </select>
          {canDo(currentUser, "services", "add") && (
            <button className="open-panel-btn" onClick={openPanel}>Add Service</button>
          )}
        </div>
      </div>
      <div className="services-list">
        {services.length === 0 ? (
          <p>No services defined yet.</p>
        ) : filteredServices.length === 0 ? (
          <p>No services match your search.</p>
        ) : (
          <table className="services-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="num-cell">Price</th>
                <th>Price Type</th>
                <th className="num-cell">Duration</th>
                <th>Category</th>
                <th>Treatment</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedServices.map((s, idx) => (
                <tr key={s.publicId || s.id || idx}>
                  <td data-label="Name">{s.name}</td>
                  <td data-label="Price" className="num-cell">{renderPriceLabel(s)}</td>
                  <td data-label="Price Type">{String(s.priceType || "fixed")}</td>
                  <td data-label="Duration" className="num-cell">{Number(s.durationMinutes || 30)} mins</td>
                  <td data-label="Category">{s.category || "-"}</td>
                  <td data-label="Treatment">{s.treatmentType || "-"}</td>
                  <td data-label="Note">{s.note}</td>
                  <td data-label="Actions" className="actions-cell">
                    {canDo(currentUser, "services", "edit") && (
                    <button
                      className="icon-btn edit-btn"
                      title="Edit"
                      onClick={() => {
                        setEditingId(s.publicId || String(s.id));
                        setForm({
                          name: s.name || "",
                          price: s.price ?? "",
                          priceType: String(s.priceType || "fixed"),
                          durationMinutes: String(s.durationMinutes || 30),
                          category: s.category || "",
                          treatmentType: s.treatmentType || "",
                          note: s.note || ""
                        });
                        setRecordAudit({ createdBy: s.createdBy || "", createdAt: s.createdAt || "", updatedBy: s.updatedBy || "", updatedAt: s.updatedAt || "" });
                        openPanel();
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="#fff" opacity="0.9"/>
                        <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="#fff" opacity="0.9"/>
                      </svg>
                    </button>
                    )}
                    {canDo(currentUser, "services", "delete") && (
                    <button
                      className="icon-btn delete-btn"
                      title="Delete"
                      onClick={async () => {
                          if (!window.confirm("Delete this service?")) return;
                          try {
                            const res = await apiFetch(`/api/services/${s.publicId || s.id}`, {
                              method: "DELETE"
                            });
                            if (!res.ok) throw new Error("Delete failed");
                            setServices(services.filter(x => (x.publicId || x.id) !== (s.publicId || s.id)));
                          } catch (err) {
                            console.error("Failed to delete", err);
                            alert("Could not delete service");
                          }
                        }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z" fill="#fff" opacity="0.95"/>
                        <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#fff" opacity="0.95"/>
                      </svg>
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginator
          page={servicesPage}
          pageSize={servicesPageSize}
          total={filteredServices.length}
          onPageChange={setServicesPage}
          onPageSizeChange={size => { setServicesPageSize(size); setServicesPage(1); }}
        />
      </div>
      <SidePanel isOpen={panelOpen} onClose={closePanel}>
        <div className="service-form-container">
          <h2>{editingId ? "Edit Service" : "Add New Service"}</h2>
          <form className="service-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="name"
              placeholder="Service name"
              value={form.name}
              onChange={handleChange}
              required
            />
            <input
              type="number"
              name="price"
              placeholder="Price"
              value={form.price}
              onChange={handleChange}
              disabled={form.priceType === "free"}
              required
            />
            <select
              name="priceType"
              value={form.priceType}
              onChange={handleChange}
              required
            >
              {PRICE_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <input
              type="number"
              name="durationMinutes"
              placeholder="Duration (minutes)"
              value={form.durationMinutes}
              onChange={handleChange}
              min="5"
              step="5"
              required
            />
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
            >
              <option value="">Select category</option>
              {categories.map(item => (
                <option key={item.id} value={item.name}>{item.name}</option>
              ))}
            </select>
            <select
              name="treatmentType"
              value={form.treatmentType}
              onChange={handleChange}
              required
            >
              <option value="">Select treatment type</option>
              {treatmentTypes.map(item => (
                <option key={item.id} value={item.name}>{item.name}</option>
              ))}
            </select>
            <textarea
              name="note"
              placeholder="Additional notes"
              value={form.note}
              onChange={handleChange}
            ></textarea>
            {recordAudit && (recordAudit.createdBy || recordAudit.createdAt || recordAudit.updatedBy || recordAudit.updatedAt) && (
              <div className="audit-trail">
                {(recordAudit.createdBy || recordAudit.createdAt) && (
                  <span className="audit-item"><strong>Created by:</strong> {recordAudit.createdBy || "\u2014"}{recordAudit.createdAt ? ` \u00b7 ${new Date(recordAudit.createdAt).toLocaleString()}` : ""}</span>
                )}
                {(recordAudit.updatedBy || recordAudit.updatedAt) && (
                  <span className="audit-item"><strong>Updated by:</strong> {recordAudit.updatedBy || "\u2014"}{recordAudit.updatedAt ? ` \u00b7 ${new Date(recordAudit.updatedAt).toLocaleString()}` : ""}</span>
                )}
              </div>
            )}
            <div className="service-form-actions">
              <button type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : (editingId ? 'Save Service' : 'Create Service')}</button>
              {editingId && canDo(currentUser, "services", "delete") && (
                <button
                  type="button"
                  className="service-delete-btn"
                  onClick={async () => {
                    if (!window.confirm('Delete this service?')) return;
                    try {
                      const res = await apiFetch(`/api/services/${editingId}`, { method: 'DELETE' });
                      if (!res.ok) throw new Error('Delete failed');
                      setServices(services.filter(x => (x.publicId || x.id) !== editingId));
                      closePanel();
                    } catch (err) {
                      console.error('Failed to delete', err);
                      alert('Could not delete service');
                    }
                  }}
                >
                  Delete Service
                </button>
              )}
            </div>
          </form>
        </div>
      </SidePanel>
    </div>
  );
};

export default Services;
